import type { UserTokenData } from '~/types/auth'
import { useQueryClient } from '@tanstack/vue-query'
import { useSentry } from '~/composables/monitoring/useSentry'
import { useMonitoring } from '~/composables/monitoring/useMonitoring'
import { useAssetCache } from '~/composables/map/useAssetCache'
import { useBeacon } from '~/composables/useBeacon'

interface UseAuthenticationReturn {
  logUserIn: (tokenData: UserTokenData) => void
  logUserOut: () => void
}

export const useAuthentication = (): UseAuthenticationReturn => {
  const queryClient = useQueryClient()
  const assetCache = useAssetCache()
  const { setUserContext } = useSentry()
  const { logEvent } = useMonitoring()

  const logUserIn = ({ access_token, expires_in, token_type }: UserTokenData): void => {
    const authStore = useAuthStore()

    if (authStore.user) {
      setUserContext(authStore.user)
      logEvent('User logged in', 'log', { method: 'password' })
    }

    const expiresAt = new Date()
    expiresAt.setSeconds(expiresAt.getSeconds() + expires_in)
    authStore.setToken(access_token, expiresAt, token_type)
  }

  const logUserOut = (): void => {
    const interfaceStore = useInterfaceStore()
    const assetStore = useAssetStore()
    const authStore = useAuthStore()
    const filterStore = useFilterStore()
    const mapStore = useMapStore()
    const beacon = useBeacon()

    if (authStore.sessionId) {
      beacon
        .sendUserActivity({ session_id: authStore.sessionId, event_type: 'logout', metadata: {} })
        .catch((err) => console.error('Failed to send logout activity', err))
    }

    if (authStore.user) {
      setUserContext(authStore.user)
      logEvent('User logged out', 'info', { userId: authStore.user.id.toString() })
    }

    // Add here other stores to reset after logout
    assetCache.clearAll()
    authStore.$reset()
    assetStore.$reset()
    interfaceStore.$reset()
    filterStore.$reset()
    mapStore.$reset()

    queryClient.clear()
  }

  return {
    logUserIn,
    logUserOut
  }
}
